<script setup lang="ts">
import { Mate } from "@/types/entity";
import { MateSprite } from "@/types/game";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import {
  AssetFileTypeEnum,
  DialogueModeEnum,
} from "../../composable/dialogue/DialogueEnum";
import { IDialogueConfig, IOption } from "../../composable/dialogue/types";
import { useDialogue } from "../../composable/dialogue/useDialogue";
import { useTyping } from "../../composable/dialogue/useTyping";

/**
 * DialogueBox
 * 不负责播放音乐，音乐播放组件的维护放在外部，避免由于DialogueBox的销毁导致音乐播放中断
 */

const props = withDefaults(
  defineProps<{
    dialogueConfig: IDialogueConfig;
    spriteList: MateSprite[];
    mateList: Mate[];
    mineMate?: Mate;
    enableClick?: boolean;
  }>(),
  {
    enableClick: true,
  }
);

const emit = defineEmits<{
  (e: "over"): void;
  (e: "update:dialogueConfig", config: IDialogueConfig): void;
  (e: "update:isPlaying", isPlaying: boolean): void;
  (e: "audioChange", audioUrl: string): void;
  (e: "toggleBgm", toggle: boolean): void;
}>();

const dialogueBoxRef = ref<HTMLElement | null>(null);
const {
  currentSentence,
  isOver,
  next,
  startNextDialogue,
  totalSentenceCount,
  sentenceIndex,
} = useDialogue(
  computed({
    get: () => props.dialogueConfig,
    set: (value) => emit("update:dialogueConfig", value),
  }),
  {
    mateListRef: computed(() => props.mateList),
    mineMateRef: computed(() => props.mineMate || undefined),
    spriteListRef: computed(() => props.spriteList),
    dialogueBoxRef,
  }
);

const endingCgVideoRef = ref<HTMLVideoElement | null>(null);
const endingCgVideoUrl = computed(() => {
  const lastIndex = props.dialogueConfig.sentences.length - 1;
  const lastSentence = props.dialogueConfig.sentences[lastIndex];
  return lastSentence?.assets?.cg?.url;
});
const isEnding = computed(() => {
  return sentenceIndex.value === totalSentenceCount.value - 1;
});

// 使用打字机效果
const { displayText, isPlaying, play, goToEnd } = useTyping(
  computed(() => currentSentence.value?.content || ""),
  { speed: 30 }
);

// 监听对话内容变化，开始打字效果
watch(
  () => currentSentence.value?.content,
  (newContent) => {
    if (newContent) {
      play();
    }
  }
);

watch(isOver, (newIsOver) => {
  if (newIsOver) {
    emit("over");
  }
});

onMounted(async () => {
  await nextTick();
  play();
});

watch(
  isPlaying,
  () => {
    emit("update:isPlaying", isPlaying.value);
  },
  {
    immediate: true,
    flush: "pre",
  }
);

const handleClick = () => {
  if (!props.enableClick) return;
  if (isPlaying.value) {
    // 如果正在打字，则直接显示完整文本
    goToEnd();
  } else {
    // 如果已经显示完整，则进入下一句对话
    next();
    if (isEnding.value && endingCgVideoUrl.value) {
      endingCgVideoRef.value?.play();
    }
  }
};

const currentFigure = computed(() => {
  return currentSentence.value?.assets?.figure;
});
const currentAudioUrl = computed(() => {
  return currentSentence.value?.assets?.audio?.url ?? "";
});

watch(
  currentAudioUrl,
  (newUrl) => {
    emit("audioChange", newUrl);
  },
  { immediate: true }
);

const isOptionLoading = ref<number | null>(null);
const handleOptionClick = async (option: IOption, idx: number) => {
  if (option.callback) {
    isOptionLoading.value = idx;
    try {
      await option.callback();
    } finally {
      isOptionLoading.value = null;
    }
  }
  let nextDialogue: IDialogueConfig | null = null;
  const nextByOptionConfig = currentSentence.value?.optionConfig?.[option.key];
  if (option.nextDialogue) {
    nextDialogue = option.nextDialogue;
  } else if (nextByOptionConfig && option.key) {
    nextDialogue = nextByOptionConfig;
  }
  if (nextDialogue) {
    startNextDialogue(nextDialogue);
    play();
  }
};

const cgVideoRef = ref<HTMLVideoElement | null>(null);
const currentVideoRef = computed(() => {
  return isEnding.value ? endingCgVideoRef.value : cgVideoRef.value;
});
watch(
  currentSentence,
  (cur, prev) => {
    if (!cur) return;
    if (cur.assets?.cg) {
      emit("toggleBgm", false);
    } else if (prev?.assets?.cg) {
      emit("toggleBgm", true);
    }
  },
  { immediate: true, flush: "post" }
);

// 添加音量渐强效果函数
const fadeInVolume = (duration: number = 3000, targetVolume: number = 0.8) => {
  const video = currentVideoRef.value;
  if (!video) return;
  video.muted = false;
  video.volume = 0;
  const startTime = performance.now();

  const updateVolume = (currentTime: number) => {
    if (video.paused) return;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    video.volume = Math.max(0, Math.min(progress * targetVolume, targetVolume));

    if (progress < 1) {
      requestAnimationFrame(updateVolume);
    }
  };

  requestAnimationFrame(updateVolume);
};

// // 监听视频元素的引用变化
// watch(cgVideoRef, (video) => {
//   if (video) {
//     video.addEventListener("play", () => {
//       fadeInVolume(video);
//     });
//   }
// });
const onCgEnded = () => {
  next(true);
};
</script>

<template>
  <div
    class="grid grid-rows-[1fr_auto] overflow-hidden items-center w-full h-full pointer-events-auto"
    :class="{
      'pointer-events-none': isOptionLoading !== null,
      'pointer-events-auto': isOptionLoading === null,
    }"
    @click="handleClick"
  >
    <div
      v-if="currentSentence?.assets?.bg || currentSentence?.assets?.cg"
      class="absolute top-0 left-0 w-full h-full bg-black"
    >
      <img
        :src="currentSentence?.assets?.bg?.url"
        alt="bg"
        class="w-full h-full object-contain"
      />
      <video
        ref="cgVideoRef"
        v-if="currentSentence?.assets?.cg && !isEnding"
        :src="currentSentence?.assets?.cg?.url"
        alt="cg"
        class="w-full h-full object-cover absolute inset-0 bg-black"
        playsinline
        autoplay
        @ended="onCgEnded"
        @play="fadeInVolume()"
        muted
      />
      <video
        ref="endingCgVideoRef"
        v-if="endingCgVideoUrl"
        :src="endingCgVideoUrl"
        alt="cg"
        class="w-full h-full object-cover absolute inset-0 bg-black"
        :class="{
          hidden: !isEnding,
        }"
        playsinline
        @ended="onCgEnded"
        @play="fadeInVolume()"
        muted
      />
      <button
        v-if="currentSentence?.assets?.cg?.url"
        class="absolute top-2 right-2 btn z-20 btn-sm btn-ghost rounded text-white hover:text-white"
        @click.stop="next(true)"
      >
        {{ $t("Buttons.skip") }} >>
      </button>
    </div>
    <div class="flex items-center justify-center w-full h-full relative z-10">
      <div
        v-if="currentFigure"
        class="w-[35%] aspect-[3/4] rounded-lg border-2 border-[#4a4a8a] flex items-center justify-center overflow-hidden"
        style="background: rgba(0, 0, 32, 0.95)"
      >
        <img
          v-if="currentFigure.fileType === AssetFileTypeEnum.IMAGE"
          :src="currentFigure.url"
          alt="figure"
          class="max-w-full max-h-full object-cover pointer-events-none"
        />
        <video
          v-else
          :src="currentFigure.url"
          alt="figure"
          class="max-w-full max-h-full object-cover"
          playsinline
          autoplay
          muted
          loop
        />
      </div>
    </div>
    <div
      v-if="currentSentence?.options"
      class="flex justify-end mb-1 relative z-10"
    >
      <div class="flex flex-col items-end w-1/2 font-[Cubic] text-white">
        <div
          v-for="(option, index) in currentSentence?.options"
          :key="index"
          class="dialog-option w-full text-xs xs:text-sm flex items-center gap-1"
          @click="handleOptionClick(option, index)"
        >
          <div>{{ option.content }}</div>
          <div
            v-if="isOptionLoading === index"
            class="loading loading-xs xs:text-xs"
          ></div>
        </div>
      </div>
    </div>
    <div
      :class="{
        invisible: currentSentence?.mode === DialogueModeEnum.HIDE_DIALOGUE,
      }"
      class="font-[Cubic] relative"
    >
      <div
        v-if="currentSentence?.assets?.portrait?.url"
        class="dialogue-portrait absolute -top-2 left-0 h-[400px] -translate-y-[50%] -translate-x-[20%] aspect-[3/4] overflow-hidden"
      >
        <img
          :src="currentSentence?.assets?.portrait?.url"
          alt="avator"
          class="w-full h-full object-cover"
        />
      </div>
      <div
        v-else-if="currentSentence?.assets?.avatar?.url"
        class="dialogue-avatar absolute -top-2 left-2 h-full -translate-y-[100%] aspect-[3/4] overflow-hidden"
      >
        <img
          :src="currentSentence?.assets?.avatar?.url"
          alt="avator"
          class="w-full h-full object-cover"
        />
      </div>
      <div
        class="dialog-box font-[Cubic] text-xs xs:text-sm mx-2 mb-2 h-[114px] max-h-[114px] cursor-pointer relative z-10"
      >
        <div ref="dialogueBoxRef" class="dialog-content p-2 font-[system-ui]">
          <div v-if="currentSentence?.charName" class="speaker">
            {{ currentSentence?.charName }}:
          </div>
          <div class="message">{{ displayText }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
<style>
.dialog-box {
  background: rgba(0, 0, 32, 0.95);
  border: 2px solid #4a4a8a;
  border-radius: 8px;
  color: white;
  font-family: "Cubic", system-ui, -apple-system, sans-serif;
  pointer-events: auto;
}

.dialog-content {
  margin: 0 auto;
}

.speaker {
  color: #8a8aff;
  font-weight: bold;
  margin-bottom: 8px;
}

.message {
  margin-bottom: 16px;
  line-height: 1.5;
  @apply whitespace-pre-line;
}

.dialog-option {
  border: 2px solid #4a4a8a;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 4px;
  cursor: pointer;
  background: rgba(0, 0, 32, 0.95);
}

.dialogue-avatar {
  border: 2px solid #4a4a8a;
  border-radius: 8px;
  background: rgba(0, 0, 32, 0.95);
}
.dialogue-portrait {
}
</style>
